import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Input, Select } from '~/components/atoms';
import { Form, Col, Row } from '~/components/molecules';
import { DOCUMENT_OPERATOR, DOCUMENT_TYPES } from '~/constants';
import * as Yup from 'yup';
import { useFormHook } from '~/hooks';
import { BoxFilters, BoxFiltersContainerButton } from '~/components/styled';
import { useFieldArray } from 'react-hook-form';
import {
  AddContainer,
  Line,
  OperatorContent,
  SpecialRow,
  TrashContainer,
} from '~/modules/Main/Association/ModalAccount/style';
import { Trash } from 'phosphor-react';

function Filter({ params, setParams }) {
  const { t } = useTranslation('association');

  const refCountry = useRef();
  const refDocumentOperator = useRef();

  const REQUIRED_FIELD = `${t('requiredField')}`;
  const DEFAULT_MIXED = Yup.mixed().required(REQUIRED_FIELD).nullable();
  const DEFAULT_STRING = Yup.string().required(REQUIRED_FIELD).nullable();

  const schema = Yup.object().shape({
    country: Yup.mixed(),
    state: Yup.string(),
    city: Yup.string(),
    documentOperator: Yup.mixed().test(
      'validate-documentOperator',
      REQUIRED_FIELD,
      (value, ctx) => {
        return !(ctx.parent.documents.length > 1 && !value);
      },
    ),
    documents: Yup.array().of(
      Yup.object().shape({
        type: DEFAULT_MIXED,
        value: DEFAULT_STRING,
      }),
    ),
  });

  const COUNTRIES = [
    {
      value: 'BR',
      label: 'BRASIL',
    },
  ];

  const { formFunctions } = useFormHook(schema, { country: COUNTRIES[0].value });

  const {
    register,
    reset,
    control,
    formState: { errors },
  } = formFunctions;

  const {
    fields: fieldsDocument,
    append: appendDocument,
    remove: removeDocument,
  } = useFieldArray({ control, name: 'documents' });

  const addDocumentFields = () => {
    appendDocument({
      type: null,
      value: null,
    });
  };
  const remove = (index) => {
    removeDocument(index);
  };

  const clearDocuments = () => {
    fieldsDocument.forEach((elm, index) => {
      removeDocument(index);
    });
  };

  const applyFilters = (obj) => {
    let paramsFilter = {};
    for (const item in obj) {
      if (obj[item] !== '') {
        paramsFilter = { ...paramsFilter, [item]: obj[item] };
      }
    }

    const newParams = { ...params, ...paramsFilter, page: 1 };

    Object.keys(newParams).forEach((k) => {
      if (newParams[k] === undefined || newParams[k] === null || newParams[k] === '')
        delete newParams[k];
    });

    setParams(newParams);
  };

  return (
    <BoxFilters>
      <Form onSubmit={applyFilters} schema={schema} formFunctions={formFunctions} withCustomSubmit>
        <Row>
          <Col className="is-4">
            <Select
              ref={refCountry}
              label={t('country')}
              value={COUNTRIES[0]}
              options={COUNTRIES}
              name="country"
              register={register}
            />
          </Col>

          <Col className="is-4">
            <Input name="state" label={t('state')} register={register} />
          </Col>

          <Col className="is-4">
            <Input name="city" label={t('city')} register={register} />
          </Col>

          <Col className="is-12">
            <Line />
          </Col>

          <Col className="is-12">
            <Row>
              <Col className="is-8">
                {fieldsDocument.map((elm, index) => (
                  <SpecialRow key={elm.id}>
                    <Col className="is-1">
                      <TrashContainer>
                        <Trash onClick={() => remove(index)} />
                      </TrashContainer>
                    </Col>

                    <Col className="is-5">
                      <Select
                        label={t('documentType')}
                        options={DOCUMENT_TYPES}
                        name={`documents.${index}.type`}
                        register={register}
                        error={errors?.documents?.[index]?.type}
                      />
                    </Col>

                    <Col className="is-6">
                      <Input
                        label={t('document')}
                        register={register}
                        name={`documents.${index}.value`}
                        error={errors?.documents?.[index]?.value}
                      />
                    </Col>
                  </SpecialRow>
                ))}
              </Col>
              <Col className="is-4">
                <OperatorContent hide={fieldsDocument.length < 2}>
                  <Select
                    ref={refDocumentOperator}
                    label={t('documentOperator')}
                    options={DOCUMENT_OPERATOR}
                    name="documentOperator"
                    register={register}
                    error={errors?.documentOperator}
                  />
                </OperatorContent>
              </Col>
            </Row>
          </Col>

          <Col className="is-12">
            <AddContainer marginTop={fieldsDocument.length > 0}>
              <Button title={t('addDocumentFields')} onClick={addDocumentFields} />
            </AddContainer>
          </Col>

          {fieldsDocument.length > 0 && (
            <Col className="is-12">
              <Line />
            </Col>
          )}
        </Row>

        <Row>
          <Col className="is-6">
            <BoxFiltersContainerButton>
              <Button type="submit" variant="secondary" fullWidth title={t('apllyFilters')} />
            </BoxFiltersContainerButton>
          </Col>
          <Col className="is-6">
            <BoxFiltersContainerButton>
              <Button
                variant="error"
                onClick={() => {
                  reset();
                  refDocumentOperator.current.resetField();
                  clearDocuments();
                  applyFilters({
                    country: COUNTRIES[0].value,
                    state: null,
                    city: null,
                    documentOperator: null,
                    documents: [],
                  });
                }}
                fullWidth
                title={t('clearFilters')}
              />
            </BoxFiltersContainerButton>
          </Col>
        </Row>
      </Form>
    </BoxFilters>
  );
}

export default Filter;
